import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '../../../../../../shared/card/card'
import { Pill } from '../../../../../../shared/sso-workspace/pill'
import { LocalTime } from '../../../../../../shared/date/local-time-view'
import { serverMessage } from '../../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'
import { useRoleSysAiToolsTelemetry } from '../../../../../../role/hook/role-sys'
import { aiToolsAvailable, getLoginConsent } from '../../../ai-tools/ai-tools-api'
import { sourceText } from '../../../ai-tools/ai-tools-words'

// A person's AI tools sharing answers on their staff page (consent
// visibility, owner-approved 2026-10-23): Shares / Doesn't share / Never
// asked / Account deleted, when they answered, from which tool and its
// version, their answers newest first, and whether each of their
// organizations blocks sharing. Staff alone see who shares: nobody else is
// told, not even as a count.
// rapid-api-ai's GET /admin/consent: staff with SYS_AI_TOOLS_TELEMETRY read,
// signed in as themselves (the server refuses an impersonated session).

const STATUS = {
  sharing: { tone: 'ok', text: 'Shares' },
  not_sharing: { tone: '', text: "Doesn't share" },
  never_asked: { tone: '', text: 'Never asked' },
  account_deleted: { tone: 'bad', text: 'Account deleted' }
}

export const choiceText = choice => (choice === 'share' ? 'Share' : choice === 'stopped' ? 'Stopped sharing' : choice || '')

// Which tool an answer came from, and its version. The tools always name
// themselves; an answer with no tool was given in the admin's account
// settings (or by a client that didn't say).
export const answeredFrom = answer => {
  if (!answer || !answer.source) return 'Not recorded (the admin\'s account settings name no tool)'
  return answer.clientVersion ? `${sourceText(answer.source)} ${answer.clientVersion}` : sourceText(answer.source)
}

// Why a person who answered doesn't share.
const notSharingWhy = (view) => {
  const c = view.current
  if (!c || view.consentStatus !== 'not_sharing') return null
  if (c.choice === 'share') {
    return `They answered question ${c.questionVersion}; the tools ask again for question ${view.minQuestionVersion}, so nothing is shared until they do.`
  }
  return 'They stopped sharing: nothing more is collected, and what they had shared is no longer linked to their account.'
}

export const LoginAIToolsSharing = ({ loginID }) => {
  const role = useRoleSysAiToolsTelemetry()
  if (!loginID || !role.accessRead || !aiToolsAvailable()) return null
  return (
    <section aria-label="AI tools sharing">
      <Card title="AI tools sharing">
        <LoginAIToolsSharingBody loginID={loginID} />
      </Card>
    </section>
  )
}

const LoginAIToolsSharingBody = ({ loginID }) => {
  const [view, setView] = useState(undefined)
  const [error, failure, setError] = useErrorLine()

  useEffect(() => {
    let live = true
    setView(undefined)
    Promise.resolve().then(() => getLoginConsent(loginID)).then(
      v => { if (live) { setError(null); setView(v || null) } },
      err => { if (live) { setView(null); setError(serverMessage(err, "This person's AI tools sharing answers couldn't be loaded. Try again in a moment."), err) } }
    )
    return () => { live = false }
  }, [loginID, setError])

  if (error) {
    return (
      <div className="m-2">
        <p className="text-danger small mb-1" role="alert">{error}</p>
        <ErrorReference error={failure} />
      </div>
    )
  }
  if (view === undefined) return <p className="text-body-secondary small m-2" role="status">Loading…</p>
  if (!view) return null

  const deleted = view.consentStatus === 'account_deleted'
  const status = STATUS[view.consentStatus] || STATUS.never_asked
  const current = view.current
  const why = notSharingWhy(view)
  const history = view.history || []
  const organizations = view.organizations || []

  return (
    <div className="m-2" data-testid="login-ai-tools-sharing">
      <p className="mb-1" data-testid="login-ai-tools-status"><Pill tone={status.tone}>{status.text}</Pill></p>
      {current &&
        <dl className="sw-dl small mb-2">
          <dt>Answered</dt>
          <dd><LocalTime value={current.answeredAt} /></dd>
          <dt>From</dt>
          <dd>{answeredFrom(current)}</dd>
          <dt>Question</dt>
          <dd>Version {current.questionVersion}</dd>
          {current.organizationName &&
            <>
              <dt>Organization</dt>
              <dd>{current.organizationName}</dd>
            </>}
        </dl>}
      {deleted && <p className="sw-hint small mb-2">Their account was deleted; its sharing answers go with it.</p>}
      {!current && !deleted && <p className="sw-hint small mb-2">They haven't answered the AI tools sharing question.</p>}
      {why && <p className="sw-hint small mb-2">{why}</p>}

      <p className="fw-semibold small mb-1 mt-3">Their organizations</p>
      {organizations.length === 0 && <p className="sw-hint small mb-2">Not a member of any organization.</p>}
      {organizations.length > 0 &&
        <ul className="list-unstyled small mb-2" aria-label="Their organizations">
          {organizations.map(o =>
            <li key={o.organizationId} className="d-flex align-items-center gap-2 mb-1">
              <Link to={`/admin/organization/list/edit/${o.organizationId}`}>{o.name || `#${o.organizationId}`}</Link>
              {o.personal && <span className="text-body-secondary">Personal</span>}
              {o.allowsSharing
                ? <Pill tone="ok">Allows sharing</Pill>
                : <Pill tone="bad" title="Nothing from work in this organization is shared, whatever they chose.">Blocks sharing</Pill>}
            </li>)}
        </ul>}

      <p className="fw-semibold small mb-1 mt-3">Their answers</p>
      {history.length === 0 && <p className="sw-hint small mb-0">None yet.</p>}
      {history.length > 0 &&
        <div className="table-responsive">
          <table className="table table-sm small mb-1" aria-label="Their answers, newest first">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Answer</th>
                <th scope="col">Question</th>
                <th scope="col">From</th>
                <th scope="col">Organization</th>
              </tr>
            </thead>
            <tbody>
              {history.map((a, i) =>
                <tr key={`${a.answeredAt}-${i}`}>
                  <td><LocalTime value={a.answeredAt} /></td>
                  <td>{choiceText(a.choice)}</td>
                  <td>{a.questionVersion}</td>
                  <td>{a.source ? answeredFrom(a) : <span className="text-body-secondary" title="The admin's account settings name no tool">Not recorded</span>}</td>
                  <td>{a.organizationName || <span className="text-body-secondary">—</span>}</td>
                </tr>)}
            </tbody>
          </table>
        </div>}
      {view.historyMore && <p className="sw-hint small mb-0">Showing their latest {history.length} answers.</p>}
    </div>
  )
}
